import { getAPIURL } from "./config";

const parseSubstitutionError = async (response, fallbackMessage) => {
  const text = await response.text();
  let message = fallbackMessage;
  try {
    const err = JSON.parse(text);
    if (Array.isArray(err.detail)) {
      message = err.detail
        .map((d) => d.msg || JSON.stringify(d))
        .join("; ");
    } else if (err.detail) {
      message = String(err.detail);
    } else if (err.message) {
      message = err.message;
    }
  } catch {
    if (text) message += `. ${text}`;
  }
  throw new Error(message);
};

/**
 * Список замещений с пагинацией и фильтрацией
 * GET /substitutions/
 * @param {{
 *   page?: number,
 *   page_size?: number,
 *   include_inactive?: boolean,
 *   absent_user_id?: number,
 *   substitute_user_id?: number,
 * }} [params]
 */
export const getSubstitutions = async (params = {}) => {
  const searchParams = new URLSearchParams();
  searchParams.set("page", String(params.page ?? 1));
  searchParams.set("page_size", String(params.page_size ?? 50));
  searchParams.set(
    "include_inactive",
    String(params.include_inactive ?? false)
  );
  if (params.absent_user_id != null) {
    searchParams.set("absent_user_id", String(params.absent_user_id));
  }
  if (params.substitute_user_id != null) {
    searchParams.set("substitute_user_id", String(params.substitute_user_id));
  }

  const response = await fetch(
    `${getAPIURL()}substitutions/?${searchParams.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      credentials: "include",
    }
  );

  if (!response.ok) {
    await parseSubstitutionError(
      response,
      `Ошибка при загрузке замещений: ${response.status}`
    );
  }

  return response.json();
};

/**
 * Создание замещения
 * POST /substitutions/
 * @param {{
 *   absent_user_id: number,
 *   substitute_user_id: number,
 *   absent_user_group_id: number,
 *   substitute_user_group_id: number,
 *   start_date: string,
 *   end_date: string,
 *   reason: string,
 *   delegate_approvals: boolean,
 *   delegate_roles: boolean,
 *   comment?: string,
 * }} body
 */
export const createSubstitution = async (body) => {
  const response = await fetch(`${getAPIURL()}substitutions/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await parseSubstitutionError(
      response,
      `Ошибка при создании замещения: ${response.status}`
    );
  }

  return response.json();
};

const getExportFilename = (contentDisposition, fallback) => {
  if (!contentDisposition) {
    return fallback;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match && utf8Match[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const filenameMatch = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
  );
  if (filenameMatch && filenameMatch[1]) {
    const filename = filenameMatch[1].replace(/['"]/g, "");
    try {
      return decodeURIComponent(filename);
    } catch {
      return filename;
    }
  }

  return fallback;
};

const downloadBlob = (blob, filename) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Экспорт замещений в XLSX
 * GET /substitutions/export
 * Без date_from и date_to — все периоды.
 * Для фильтра нужны оба параметра (пересечение с периодом замещения).
 * @param {{
 *   date_from?: string,
 *   date_to?: string,
 *   include_inactive?: boolean,
 * }} [params]
 */
export const exportSubstitutionsExcel = async (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.date_from && params.date_to) {
    searchParams.set("date_from", params.date_from);
    searchParams.set("date_to", params.date_to);
  }
  if (params.include_inactive) {
    searchParams.set("include_inactive", "true");
  }

  const query = searchParams.toString();
  const response = await fetch(
    `${getAPIURL()}substitutions/export${query ? `?${query}` : ""}`,
    {
      method: "GET",
      headers: {
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/octet-stream, application/json",
      },
      credentials: "include",
    }
  );

  if (!response.ok) {
    await parseSubstitutionError(
      response,
      `Ошибка при выгрузке замещений: ${response.status}`
    );
  }

  const fallbackName =
    params.date_from && params.date_to
      ? `Замещения_${params.date_from}_${params.date_to}.xlsx`
      : "Замещения.xlsx";
  const filename = getExportFilename(
    response.headers.get("content-disposition"),
    fallbackName
  );

  downloadBlob(await response.blob(), filename);
};
